import { useQuery } from '@tanstack/react-query';
import { STRICT_PASSWORD_POLICY, type PasswordPolicy } from '@lognai/plugin-sdk';

/**
 * The password rules the server actually enforces.
 *
 * Every form that sets a password used to carry its own guess at them — this
 * page checked length alone, against a server that also requires an uppercase
 * letter, a lowercase letter, a digit, a symbol and a password that is not on
 * the common list (QA ledger AUTH-008).
 *
 * Public and unauthenticated by design: an invitee has no session yet. Falls
 * back to the STRICT policy while loading and on failure, so a form that
 * guesses refuses a password the server would take rather than accepting one it
 * will reject.
 */
export function usePasswordPolicy(): PasswordPolicy {
  const { data } = useQuery({
    queryKey: ['auth', 'password-policy'],
    queryFn: async (): Promise<PasswordPolicy> => {
      const response = await fetch('/api/auth/password-policy');
      if (!response.ok) throw new Error('Could not read the password policy');
      const body = (await response.json()) as { success: boolean; data: PasswordPolicy };
      return body.data;
    },
    staleTime: Infinity,
    retry: false,
  });
  return data ?? STRICT_PASSWORD_POLICY;
}
